import { track } from '../lib/analytics.js';
import type { HiringStage } from '../fixtures/rules.js';

/**
 * The hiring pipeline's own events (AI-3013).
 *
 * The bulk path has reported since it shipped (`bulk_stage_set`); moving one
 * candidate — the thing employers do all day — reported nothing at all. So the
 * rare act was visible and the common one was not, and the funnel could not be
 * drawn.
 *
 * One name carrying the act as a property, mirroring `bulk_stage_set`, so the
 * single-chat and bulk funnels line up without a union query.
 *
 * No property is called `source`, `user_role` or `view_mode`: the backend
 * stamps its own values over those three after merging, so anything sent under
 * them vanishes with no error.
 */

/** Where the press happened. */
export type HiringSurface =
  | 'strip'
  | 'info_panel'
  | 'widget'
  | 'pane'
  /** The direct chat's `EndChatActions` group — ending only, no pipeline. */
  | 'header'
  | 'countdown'
  | 'assistant';

/**
 * Which control carried it. Only the widget varies: the strip and the Info
 * panel show every act at once, so both are always `button`.
 *
 * `handoff` is the widget's ending. A 400px panel does not end a chat, it hands
 * the chat to /chat with `?open=end` and lets the dialog open there — so the
 * press is the widget's, and which of its two controls carried it does not
 * survive the trip. Said outright rather than left missing, which would mean
 * two things at once.
 */
export type HiringVia = 'button' | 'menu' | 'handoff';

interface StagePress {
  stage: HiringStage;
  surface: HiringSurface;
  via: HiringVia;
  /** Bare channel id. Spelled `channel_id` on the wire — the BFF sqids-encodes
   *  that exact key, and an id in the clear gets the whole batch refused.
   *  Absent from a pane with no chat yet. */
  channelId?: string;
}

export function trackHiringStage({ stage, surface, via, channelId }: StagePress): void {
  track('hiring_stage_set', { stage, surface, via, channel_id: channelId });
}
